import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiClock, FiMonitor, FiFileText } from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalResumenFacturasSesion from './ModalResumenFacturasSesion';
import ModalResumenRetirosSesion from './ModalResumenRetirosSesion';

function TarjetaResumen({ titulo, valor, colorBorde, colorTexto }) {
  return (
    <div className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde}`}>
      <p className="text-sm text-gray-500">{titulo}</p>
      <p className={`text-2xl font-bold mt-1 ${colorTexto}`}>{valor}</p>
    </div>
  );
}

const formatMonto = (monto) => {
  return parseFloat(monto || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatFechaHora = (fecha) => {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleString('es-DO', {
    dateStyle: 'short',
    timeStyle: 'short'
  });
};

export default function DashboardMonitoreoCajas() {
  const [cajas, setCajas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [actualizando, setActualizando] = useState(false);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [modalFacturasAbierto, setModalFacturasAbierto] = useState(false);
  const [modalRetirosAbierto, setModalRetirosAbierto] = useState(false);
  const [sesionSeleccionada, setSesionSeleccionada] = useState(null);

  const cargarMonitoreo = async ({ manual = false } = {}) => {
    try {
      if (manual) {
        setActualizando(true);
      } else {
        setCargando(true);
      }

      const response = await instanciaAxios.get('/sesion-caja/monitoreo');
      setCajas(response.data?.cajas || []);
      setUltimaActualizacion(response.data?.fecha_consulta || new Date().toISOString());
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar monitoreo de cajas:', error);
      toast.error('Error al cargar el monitoreo de cajas');
    } finally {
      setCargando(false);
      setActualizando(false);
    }
  };

  useEffect(() => {
    cargarMonitoreo();
  }, []);

  const abrirModalFacturas = (caja) => {
    setSesionSeleccionada(caja);
    setModalFacturasAbierto(true);
  };

  const cerrarModalFacturas = () => {
    setModalFacturasAbierto(false);
    setSesionSeleccionada(null);
  };

  const abrirModalRetiros = (caja) => {
    setSesionSeleccionada(caja);
    setModalRetirosAbierto(true);
  };

  const cerrarModalRetiros = () => {
    setModalRetirosAbierto(false);
    setSesionSeleccionada(null);
  };

  const resumen = useMemo(() => {
    return cajas.reduce((acc, caja) => {
      acc.totalFacturado += parseFloat(caja.total_facturado || 0);
      acc.totalCobrado += parseFloat(caja.total_cobrado || 0);
      acc.totalEgresos += parseFloat(caja.total_egresos || 0);
      acc.totalRetiros += parseFloat(caja.total_retiros || 0);
      acc.efectivoEsperado += parseFloat(caja.efectivo_esperado || 0);
      return acc;
    }, {
      totalFacturado: 0,
      totalCobrado: 0,
      totalEgresos: 0,
      totalRetiros: 0,
      efectivoEsperado: 0
    });
  }, [cajas]);

  if (cargando && cajas.length === 0) {
    return <SpinnerCargando />;
  }

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-12 w-full max-w-7xl mx-10">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="space-y-1">
                <h1 className="font-cascadia font-bold text-2xl">Monitoreo de Cajas</h1>
                <p className="text-gray-500">
                  Vista en vivo (actualizaci&oacute;n manual) de ventas y movimientos por caja abierta.
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <FiClock size={13} />
                  &Uacute;ltima actualizaci&oacute;n: {ultimaActualizacion ? formatFechaHora(ultimaActualizacion) : '-'}
                </p>
              </div>

              <button
                onClick={() => cargarMonitoreo({ manual: true })}
                disabled={actualizando}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl text-sm font-medium hover:bg-color-secundario transition-colors shadow-md disabled:opacity-70"
              >
                <FiRefreshCw size={16} className={actualizando ? 'animate-spin' : ''} />
                Actualizar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <TarjetaResumen
                titulo="Cajas Abiertas"
                valor={cajas.length.toLocaleString('es-DO')}
                colorBorde="border-blue-500"
                colorTexto="text-blue-700"
              />
              <TarjetaResumen
                titulo="Total Facturado"
                valor={`RD$ ${formatMonto(resumen.totalFacturado)}`}
                colorBorde="border-green-500"
                colorTexto="text-green-700"
              />
              <TarjetaResumen
                titulo="Total Cobrado"
                valor={`RD$ ${formatMonto(resumen.totalCobrado)}`}
                colorBorde="border-emerald-500"
                colorTexto="text-emerald-700"
              />
              <TarjetaResumen
                titulo="Total Egresos + Retiros"
                valor={`RD$ ${formatMonto(resumen.totalEgresos + resumen.totalRetiros)}`}
                colorBorde="border-red-500"
                colorTexto="text-red-700"
              />
              <TarjetaResumen
                titulo="Efectivo Esperado"
                valor={`RD$ ${formatMonto(resumen.efectivoEsperado)}`}
                colorBorde="border-purple-500"
                colorTexto="text-purple-700"
              />
            </div>

            <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
                <FiMonitor className="text-gray-500" />
                <h2 className="font-cascadia text-lg font-semibold text-gray-700">
                  Detalle por Caja
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-color-primario text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Caja</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Cajero</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Apertura</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Facturado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Cobrado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Egresos</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Retiros</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Efectivo Esperado</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cajas.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-12 text-center">
                          <p className="text-gray-500">No hay cajas abiertas en este momento.</p>
                        </td>
                      </tr>
                    ) : (
                      cajas.map((caja) => (
                        <tr key={caja.id_sesion_caja} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{caja.nombre_caja}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{caja.cajero || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatFechaHora(caja.fecha_apertura)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">RD$ {formatMonto(caja.total_facturado)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">RD$ {formatMonto(caja.total_cobrado)}</td>
                          <td className="px-4 py-3 text-sm text-right text-red-700">RD$ {formatMonto(caja.total_egresos)}</td>
                          <td className="px-4 py-3 text-sm text-right text-purple-700">RD$ {formatMonto(caja.total_retiros)}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-blue-700">
                            RD$ {formatMonto(caja.efectivo_esperado)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-1">
                              <button
                                onClick={() => abrirModalFacturas(caja)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                <FiFileText size={13} />
                                Ver facturas
                              </button>
                              <button
                                onClick={() => abrirModalRetiros(caja)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                <FiFileText size={13} />
                                Ver retiros
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalResumenFacturasSesion
        mostrar={modalFacturasAbierto}
        onClose={cerrarModalFacturas}
        idSesionCaja={sesionSeleccionada?.id_sesion_caja}
        nombreCaja={sesionSeleccionada?.nombre_caja}
      />
      <ModalResumenRetirosSesion
        mostrar={modalRetirosAbierto}
        onClose={cerrarModalRetiros}
        idSesionCaja={sesionSeleccionada?.id_sesion_caja}
        nombreCaja={sesionSeleccionada?.nombre_caja}
      />
    </div>
  );
}
